/**
 * Socket.IO channel contract.
 *
 * Both sides import these interfaces, so adding an event without handling it is
 * a compile error rather than a silent no-op at 3am on a live channel.
 */
import type {
  AdminAck,
  AdminCommand,
  BroadcastEvent,
  BroadcastState,
  BroadcastTick,
} from './types.js';

export const SOCKET_PATH = '/live';

/** Room that receives the full firehose. Every viewer joins it. */
export const ROOM_BROADCAST = 'broadcast';
/** Room for authenticated admin panels. */
export const ROOM_ADMIN = 'admin';

export interface ServerToClientEvents {
  /** Full snapshot: sent on connect, on phase change and after any admin action. */
  'state:snapshot': (state: BroadcastState) => void;
  /** High frequency, low payload. `TICK_RATE` per second. */
  'state:tick': (tick: BroadcastTick) => void;
  /** Discrete things that deserve an animation, a sound or a line of speech. */
  'broadcast:event': (event: BroadcastEvent) => void;
  /** Server is going away (deploy, restart). Clients back off and reconnect. */
  'server:shutdown': (reason: string) => void;
}

export interface ClientToServerEvents {
  /** Admin panel authentication. */
  'admin:auth': (token: string, ack: (result: AdminAck) => void) => void;
  /** Control the run. Rejected unless the socket authenticated first. */
  'admin:command': (command: AdminCommand, ack: (result: AdminAck) => void) => void;
  /** Round trip clock probe so overlays can de-skew their local timers. */
  'time:sync': (clientSent: number, ack: (serverTime: number) => void) => void;
}

export interface InterServerEvents {
  ping: () => void;
}

export interface SocketData {
  isAdmin: boolean;
  connectedAt: number;
}
